import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";
import { getPasswordManagerAuthHeaders, passwordManagerUrl } from "./personaApi";
import { setResetStatus } from "./getResetStatuses";
import { IResetStatus } from "./resetAutomationTypes";

export interface ITriggerPasswordResetParams {
	credentialId: number;
}

interface ITriggerPasswordResetResponse {
	credentialId: number;
	attemptNumber: number;
	status: string;
	enqueuedOn: string;
}

interface ITriggerPasswordResetState {
	loading: boolean;
	error: string | null;
	inFlightCredentialId: number | null;
}

const initialState: ITriggerPasswordResetState = {
	loading: false,
	error: null,
	inFlightCredentialId: null,
};

const extractErrorMessage = (error: unknown): string => {
	if (axios.isAxiosError(error)) {
		if (error.response?.status === 409) return "A password reset is already in progress for this credential.";
		const data = error.response?.data as { message?: string; Message?: string } | string | undefined;
		if (typeof data === "string" && data.trim()) return data;
		if (data && typeof data === "object") {
			const msg = data.message || data.Message;
			if (msg) return msg;
		}
	}
	if (error instanceof Error && error.message) return error.message;
	return "Failed to trigger password reset";
};

export const triggerPasswordReset = createAsyncThunk<IResetStatus, ITriggerPasswordResetParams>(
	"passwordManager/triggerPasswordReset",
	async (params, { rejectWithValue, dispatch }) => {
		try {
			const res = await withPasswordManagerRefresh(dispatch, () =>
				axios.post<ITriggerPasswordResetResponse>(
					passwordManagerUrl(`/Credentials/${params.credentialId}/reset-password`),
					{},
					{ headers: getPasswordManagerAuthHeaders() },
				),
			);
			const status: IResetStatus = {
				credentialId: params.credentialId,
				status: "InProgress",
				failureLabel: null,
				failureMessage: null,
				lastAttemptAt: res.data?.enqueuedOn ?? null,
				requiresManualReview: false,
			};
			dispatch(setResetStatus(status));
			return status;
		} catch (error: unknown) {
			return rejectWithValue(extractErrorMessage(error));
		}
	},
);

const triggerPasswordResetSlice = createSlice({
	name: "passwordManager/triggerPasswordReset",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(triggerPasswordReset.pending, (state, action) => {
				state.loading = true;
				state.error = null;
				state.inFlightCredentialId = action.meta.arg.credentialId;
			})
			.addCase(triggerPasswordReset.fulfilled, (state) => {
				state.loading = false;
				state.inFlightCredentialId = null;
			})
			.addCase(triggerPasswordReset.rejected, (state, action) => {
				state.loading = false;
				state.inFlightCredentialId = null;
				state.error = (action.payload as string) || action.error.message || "Failed to trigger password reset";
			});
	},
});

export const triggerPasswordResetReducer = triggerPasswordResetSlice.reducer;
